import React, { useState, useEffect, useMemo } from "react";
import "chart.js/auto";
import TransferGraph from "./TransferChart";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./Transfer.css";
import { useNavigation } from "../../../../contexts/NavigationContext";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { formatWithCommas, formatSignedCurrency } from "../../utils/formatNumber";
import { useDemandChargeData } from "../../hooks/useDemandChargeData";
import { buildDemandChargePayload } from "../../utils/demandCharge";
import { transferUtilityBill } from "../Summary/cardFlowRows";
import { ABSENT_VALUE } from "../Summary/summaryCardReadiness";

export default function Transfer() {
  
  const { exportedTransferData } = useExportedData();
  const { timeSeriesFileName } = useNavigation();
  const { hasDemandCharge, dailyDemand } = useDemandChargeData();

  /* The card's figure is the UTILITY BILL -- import cost, export revenue and
   * the demand charge for the period on screen -- through the same function
   * as the Summary's Transfer card, so the two tabs cannot disagree. It was
   * `Transfer Cost ($)`, the site's net across all three categories, which
   * silently carried the generator's fuel and the storage revaluation under a
   * "Total Cost" heading on the tab named for the grid. Fuel is on the
   * Production tab. */
  const utilityBill = useMemo(() => {
    const data = exportedTransferData || {};
    /* No period, no bill.
     *
     * `exportedTransferData` is app-wide and written by whichever chart ran
     * last. OverlayChart publishes the metered cash without the period keys,
     * so from every Summary -> Transfer navigation until TransferChart's own
     * effect lands, this page holds cash and no period. `filterRowsForPeriod`
     * answers an unrecognised view with EVERY row, which would bill a single
     * day the whole run's demand charge under a heading the reader takes for
     * the period on screen. Measured on a two-month fixture: -$2,000 shown
     * where the day owed -$1,400.
     *
     * An em-dash for those renders, then, on the same rule as every other
     * absent figure on these cards: unknown is not zero, and it is not the
     * whole run either. `sumDeviceAttributionForPeriod` already refuses an
     * unresolved period for exactly this reason -- see the note on its
     * "worse than an em-dash because it looks right". */
    if (!data.periodView) return null;
    const demandData = buildDemandChargePayload(
      hasDemandCharge,
      dailyDemand,
      data.periodView,
      data.selDate || "",
      data.selMonth || "",
      data.selYear || "",
    );
    return transferUtilityBill(data, demandData);
  }, [exportedTransferData, hasDemandCharge, dailyDemand]);
  const [isChartLoading, setIsChartLoading] = useState(true);

  const rawCustomerEmail = sessionStorage.getItem("selectedCustomerEmail") || "";
  const customerEmailKey = rawCustomerEmail ? rawCustomerEmail.replace(/[@.]/g, "-") : "";
  const rawDesignName = sessionStorage.getItem("microgridDesign") || "";
  const gridName = rawDesignName
    ? rawDesignName.replace(`${customerEmailKey}-`, "") || rawDesignName
    : "Grid";
 
  const [device, setDevice] = useState({
    Device_Name: "Grid",
    power: 0,
    minPower: 0,
    totalImport: 0,
    totalExport: 0,
  });

  // Update device data when exportedTransferData changes
  useEffect(() => {
    const data = exportedTransferData || {};
    setDevice({
      Device_Name: "Grid",
      power: data.maxPower || 0,
      minPower: data.minPower || 0,
      totalImport: data.totalImport || 0,
      totalExport: data.totalExport || 0,
    });
  }, [exportedTransferData]);
  
  // Callback to receive loading state from TransferGraph
  const handleLoadingStateChange = (loading) => {
   
    setIsChartLoading(loading);
  };

  // Stats Cards Skeleton Component
  const StatsCardsSkeleton = () => (
    <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
      <div className="card-container-transfer">
        <div className="stats-container-transfer transfer-skeleton-card">
          {/* Device Name */}
          <div className="header-transfer">
            <Skeleton width={80} height={24} style={{ marginBottom: "8px" }} />
            <div className="box-transfer">
              <div className="period-transfer">
                <Skeleton width={120} height={18} />
              </div>
            </div>
          </div>

          {/* Total Cost */}
          <Skeleton
            width={100}
            height={20}
            style={{ marginBottom: "8px", marginTop: "20px" }}
          />
          <div className="transfer-cost-display">
            <Skeleton width={150} height={48} />
          </div>

          {/* Device Stats */}
          <div className="device-stats-transfer" style={{ marginTop: "20px" }}>
            <div className="stat-item-transfer">
              <Skeleton
                width={80}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={60} height={20} />
            </div>

            <div className="stat-item-transfer">
              <Skeleton
                width={90}
                height={16}
                style={{ marginBottom: "4px" }}
              />
              <Skeleton width={70} height={20} />
            </div>
          </div>
        </div>
      </div>
    </SkeletonTheme>
  );
  return (
    <div className="transfer-container">
      <div className="sticky-header">
        <h1>Transfer</h1>
       
      </div>

      <div className="sticky-cards">
        {isChartLoading ? (
          <StatsCardsSkeleton />
        ) : (
          <div className="card transfer-card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0 device-name-transfer">{gridName}</h5>
                <div className="box-transfer">
                  <span className="period-transfer">
                    {exportedTransferData?.view === "All Time View"
                      ? "All Time View"
                      : `${exportedTransferData?.view || ""}: ${exportedTransferData?.selectedPeriod || ""}`}
                  </span>
                </div>
              </div>

              <div className="text-center my-3">
                <div
                  className="stat-heading-transfer"
                  title="Import cost + export revenue + demand charge: what crossed the utility meter. Generator fuel is on the Production tab."
                >
                  Utility bill
                </div>
                <span className="transfer-number" data-testid="transfer-utility-bill">
                  {utilityBill === null
                    ? ABSENT_VALUE
                    : formatSignedCurrency(utilityBill)}
                </span>
              </div>

              <div className="row mt-3">
                <div className="col">
                  <div className="mb-2">
                    <div className="stat-heading-transfer">Export Max</div>
                    <div className="stat-value-transfer">{formatWithCommas(device.power)} kW</div>
                  </div>
                  <div>
                    <div className="stat-heading-transfer">Import Max</div>
                    <div className="stat-value-transfer">{formatWithCommas(device.minPower)} kW</div>
                  </div>
                </div>
                <div className="col d-flex flex-column justify-content-end text-end">
                  <div className="mb-2">
                    <div className="stat-heading-transfer">Export Total</div>
                    <div className="stat-value-transfer">{formatWithCommas(device.totalExport)} kWh</div>
                  </div>
                  <div>
                    <div className="stat-heading-transfer">Import Total</div>
                    <div className="stat-value-transfer">{formatWithCommas(device.totalImport)} kWh</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Graph Component */}
      {
        <TransferGraph
          csvData={timeSeriesFileName}
          
          onLoadingStateChange={handleLoadingStateChange}
        />
      }
    </div>
  );
}
